import type {
  AvailabilityEnum,
  BadgeType,
  GenericImage,
  MultipleProductCard,
  MultipleProductCardVariant,
  ProductCardPriceEnum,
  ProductCardPricing,
  ProductCardTypeEnum,
  ReviewSummaryType,
  RibbonType,
  SingularProductCard,
  SingularProductCardVariant,
  VariantAvailabilityType,
  VariantType,
  VariantTypeV2,
  VisualBynderType
} from "@shared/graphql.types";

export interface VariantToProductCardOptions {
  buttonText?: string;
  highlightedPrice?: ProductCardPriceEnum;
  store: "uk" | "us";
}

/**
 * Helper function to create ProductCardPricing from variant
 */
function productCardPricingFromVariant(
  variant: VariantType | VariantTypeV2,
  currency: string,
  currencySymbol: string
): ProductCardPricing {
  return {
    __typename: "ProductCardPricing",
    primary: {
      __typename: "ProductPricingDetailsType" as const,
      formatted: `${currencySymbol}${variant.pricing.members.toFixed(2)}`,
      label: "Member Price",
      raw: variant.pricing.members,
      currency,
      currencySymbol
    },
    secondary: {
      __typename: "ProductPricingDetailsType" as const,
      formatted: `${currencySymbol}${variant.pricing.typical.toFixed(2)}`,
      label: "Typical Price",
      raw: variant.pricing.typical,
      currency,
      currencySymbol
    }
  };
}

/**
 * Helper function to convert visual to GenericImage
 */
function visualToGenericImage(detailsMedia: VisualBynderType): GenericImage {
  return {
    __typename: "GenericImage",
    id: detailsMedia.id || "",
    altText: detailsMedia?.description || "",
    description: detailsMedia?.description || undefined,
    src:
      detailsMedia?.thumbnails?.size_460 ??
      detailsMedia?.thumbnails?.size_OTF_BETA ??
      ""
  };
}

/**
 * Helper function to get GenericImage from badge
 */
function genericImageFromBadge(badge: BadgeType): GenericImage | undefined {
  if (!badge) return undefined;

  return {
    __typename: "GenericImage",
    id: "",
    altText: badge.media?.description || "",
    description: badge.media?.description || "",
    src:
      badge.media?.thumbnails?.size_460 ??
      badge.media?.thumbnails?.size_OTF_BETA ??
      ""
  };
}

/**
 * Helper function to get ribbon from variant
 */
function getRibbonFromVariant(): RibbonType | undefined {
  // Variants don't directly have ribbons in the GraphQL schema
  // This could be enhanced to derive ribbons from variant properties
  // (e.g., new products, bestsellers, on sale, etc.)
  return undefined;
}

/**
 * Helper function to get review summary from variant
 */
function getReviewSummaryFromVariant(
  variant: VariantType | VariantTypeV2
): ReviewSummaryType {
  return {
    __typename: "ReviewSummaryType",
    count: variant.review?.count || 0,
    stars: variant.review?.stars || 0
  };
}

/**
 * Helper function to convert variant availability to product card availability
 */
function variantAvailabilityFromVariant(
  variant: VariantType | VariantTypeV2
): VariantAvailabilityType {
  const inStock =
    variant.availability?.status === ("instock" as AvailabilityEnum);

  return {
    __typename: "VariantAvailabilityType",
    inStock
  };
}

/**
 * Helper function to create localized product URL
 */
function createProductUrl(
  variant: VariantType | VariantTypeV2,
  store: "uk" | "us"
): string {
  const productSlug = variant.slug || variant.sku;

  if (variant.pdpUrl && variant.pdpUrl[store]) {
    return variant.pdpUrl[store];
  }

  return store === "us"
    ? `/us/products/${productSlug}`
    : `/products/${productSlug}`;
}

/**
 * Creates a SingularProductCard from a single variant
 */
function createSingularProductCard(
  variant: VariantType | VariantTypeV2,
  options: VariantToProductCardOptions
): SingularProductCard {
  const singularVariant: SingularProductCardVariant = {
    __typename: "SingularProductCardVariant",
    availability: variantAvailabilityFromVariant(variant),
    badge: variant.badges?.[0]
      ? genericImageFromBadge(variant.badges[0])
      : undefined,
    href: createProductUrl(variant, options.store),
    images: variant.detailsMedia
      ? [visualToGenericImage(variant.detailsMedia)]
      : [],
    key: variant.id || undefined,
    prices: variant.prices || undefined,
    pricing: productCardPricingFromVariant(
      variant,
      variant.prices
        ? variant.prices[variant.prices?.activeType].regular.currency
        : "",
      variant.prices
        ? variant.prices[variant.prices?.activeType].regular.currencySymbol
        : ""
    ),
    ribbon: getRibbonFromVariant(),
    shade: variant.title,
    sku: variant.sku,
    slug: variant.slug || variant.sku
  };

  return {
    __typename: "SingularProductCard",
    brandName: variant.brandName || undefined,
    buttonText: options?.buttonText || "Add to Bag",
    highlightedPrice: options?.highlightedPrice || undefined,
    id: variant.id || variant.sku,
    reviews: getReviewSummaryFromVariant(variant),
    title: variant.title,
    type: "plain" as ProductCardTypeEnum,
    variants: [singularVariant]
  };
}

/**
 * Creates a MultipleProductCard from multiple variants
 */
function createMultipleProductCard(
  variants: (VariantType | VariantTypeV2)[],
  options: VariantToProductCardOptions
): MultipleProductCard {
  const primaryVariant = variants[0];

  const multipleVariants: MultipleProductCardVariant[] = variants.map(
    (variant) => ({
      __typename: "MultipleProductCardVariant",
      availability: variantAvailabilityFromVariant(variant),
      badge: variant.badges?.[0]
        ? genericImageFromBadge(variant.badges[0])
        : undefined,
      href: createProductUrl(variant, options.store),
      images: variant.detailsMedia
        ? [visualToGenericImage(variant.detailsMedia)]
        : [],
      key: variant.id || undefined,
      prices: variant.prices || undefined,
      pricing: productCardPricingFromVariant(
        variant,
        variant.prices
          ? variant.prices[variant.prices?.activeType].regular.currency
          : "",
        variant.prices
          ? variant.prices[variant.prices?.activeType].regular.currencySymbol
          : ""
      ),
      ribbon: getRibbonFromVariant(),
      shade: variant.title,
      sku: variant.sku,
      slug: variant.slug || variant.sku,
      swatch: {
        __typename: "GenericImage",
        id: "",
        altText: "",
        description: undefined,
        src: variant.shadeVisual ?? ""
      }
    })
  );

  const swatchImages: GenericImage[] = variants.map((variant) => ({
    __typename: "GenericImage",
    id: "",
    altText: variant.descriptionVisual?.altTag ?? "",
    description: variant.description ?? "",
    src: variant.shadeVisual ?? ""
  }));

  return {
    __typename: "MultipleProductCard",
    brandName: primaryVariant.brandName || undefined,
    buttonText: options?.buttonText || "Add to Bag",
    highlightedPrice: options?.highlightedPrice || undefined,
    id: primaryVariant.id || primaryVariant.sku,
    reviews: getReviewSummaryFromVariant(primaryVariant),
    secondaryButton: undefined,
    swatchImages,
    title: primaryVariant.title,
    type: "multivariate" as ProductCardTypeEnum,
    variants: multipleVariants
  };
}

/**
 * Converts a VariantType or VariantTypeV2 to either a SingularProductCard or MultipleProductCard
 * based on whether the variant has relatedVariants.
 *
 * - If relatedVariants exist → MultipleProductCard with main + related variants
 * - If no relatedVariants → SingularProductCard with just the main variant
 */
export const variantToProductCard = (
  variant: VariantType | VariantTypeV2,
  options: VariantToProductCardOptions
): SingularProductCard | MultipleProductCard => {
  const relatedVariants =
    variant.relatedVariants?.filter(
      (item): item is VariantType | VariantTypeV2 =>
        item?.sku !== variant.sku && item != null
    ) ?? [];

  const hasRelatedVariants = relatedVariants.length > 0;

  if (hasRelatedVariants) {
    // Create MultipleProductCard with main variant + related variants

    const allVariants = [variant, ...relatedVariants];
    return createMultipleProductCard(allVariants, options);
  } else {
    // Create SingularProductCard with just the main variant
    return createSingularProductCard(variant, options);
  }
};
